import React, { useState, useEffect, useRef } from "react";

const SearchResultOption = ({
  option,
  onStyleSelect,
  searchValue,
  isSelected,
  index,
  onMouseEnter,
}) => {
  const optionName = `${option.master_style} - ${option.master_brand}`;
  const optionRef = useRef(null);
  
  // Scroll into view if selected
  useEffect(() => {
    if (isSelected && optionRef.current) {
      optionRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isSelected]);
  
  // Create a function to highlight matching text
  const highlightMatch = (text, searchValue) => {
    if (!searchValue) return <span className="font-normal" style={{ fontSize: '0.9rem' }}>{text}</span>;
    
    const parts = text.split(new RegExp(`(${searchValue})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchValue.toLowerCase() ? 
        <span key={index} className="font-bold" style={{ fontSize: '0.9rem' }}>{part}</span> : 
        <span key={index} className="font-normal" style={{ fontSize: '0.9rem' }}>{part}</span>
    );
  };

  return (
    <div
      ref={optionRef}
      className={`
        flex items-center px-[10px] py-[6px]
        w-full 
        ${isSelected ? 'bg-lightestGrey' : 'hover:bg-lightestGrey'}
        cursor-pointer text-wolfGrey
      `}
      style={{ fontSize: '1rem' }}
      onClick={() => {
        onStyleSelect(option);
      }}
      onMouseEnter={() => onMouseEnter(index)}
    >
      <div className="flex items-center" style={{ fontSize: '1rem', marginLeft: '24px' }}>
        {highlightMatch(optionName, searchValue)}
      </div>
    </div>
  );
};

const FastEntryStyleResults = ({
  filteredNewRecommendations,
  autoCompleteSuggestions,
  inputValue,
  onStyleSelect,
  showAutoSearchResults = false,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const resultsContainerRef = useRef(null);
  
  // Determine which results to show
  const resultsToShow = inputValue === "" 
    ? (showAutoSearchResults ? autoCompleteSuggestions : filteredNewRecommendations)
    : autoCompleteSuggestions.slice(0, 7);
  
  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [resultsToShow.length, inputValue]);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!resultsToShow.length) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prevIndex => {
            const nextIndex = prevIndex < resultsToShow.length - 1 ? prevIndex + 1 : 0;
            return nextIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prevIndex => {
            const nextIndex = prevIndex > 0 ? prevIndex - 1 : resultsToShow.length - 1;
            return nextIndex;
          });
          break;
        case 'Tab':
          // Prevent default tab behavior to keep focus in the input
          e.preventDefault();
          setSelectedIndex(prevIndex => {
            const nextIndex = prevIndex < resultsToShow.length - 1 ? prevIndex + 1 : 0;
            return nextIndex;
          });
          break;
        case 'Enter':
          if (selectedIndex >= 0 && selectedIndex < resultsToShow.length) {
            e.preventDefault();
            onStyleSelect(resultsToShow[selectedIndex]);
          }
          break;
        default:
          break;
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);
    
    // Clean up event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [resultsToShow, selectedIndex, onStyleSelect]);

  const handleMouseEnter = (index) => {
    setSelectedIndex(index);
  };

  return (
    <div 
      ref={resultsContainerRef} 
      className="w-full h-full overflow-y-auto rounded-bl-[25px] rounded-br-[25px]"
      style={{ fontSize: '1rem' }}
    >
      {resultsToShow.map((option, index) => (
        <SearchResultOption
          key={`${inputValue === "" ? "auto" : "search"}-${index}`}
          option={option}
          onStyleSelect={onStyleSelect}
          searchValue={inputValue}
          isSelected={index === selectedIndex}
          index={index}
          onMouseEnter={handleMouseEnter}
        />
      ))}
    </div>
  );
};

export default FastEntryStyleResults;